import { type Event, EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';

import { getClientConfig } from 'config/clients';
import { type PtData, type PtItem } from 'typedefs/playTrackerTypes';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';
import { formatDateTime } from 'utils/dateTimeUtils';

import { type FormatType, ListTypeEnum } from './rankingsAndRatings/types';

interface RanklistInterface {
  id: string;
  publishDate: string;
}

export interface RankListResult {
  ranklists: RanklistInterface[];
}

interface Option {
  label: string;
  value: string;
}

enum GenderEnum {
  MALE = 'M',
  FEMALE = 'F',
  MIXED = 'X',
}

enum WtnType {
  SINGLES = 'SINGLE',
  DOUBLES = 'DOUBLE',
}

export const getFormatOptions = (
  isJunior: boolean,
  listType: ListTypeEnum | '',
  formatType: FormatType,
  isMixed: boolean,
  isWheelchair: boolean,
): Option[] => {
  if (isJunior) {
    if (listType === ListTypeEnum.STANDING) {
      return [
        {
          label: 'Combined',
          value: 'COMBINED',
        },
      ];
    } else if (listType === ListTypeEnum.SEEDING) {
      return [
        {
          label: 'Doubles',
          value: EventTypeEnum.DOUBLES,
        },
        {
          label: 'Singles',
          value: EventTypeEnum.SINGLES,
        },
      ];
    }
  } else {
    if (formatType === 'INDIVIDUAL') {
      if (isMixed && !isWheelchair) {
        return [
          {
            label: 'Doubles',
            value: EventTypeEnum.DOUBLES,
          },
          {
            label: 'Singles',
            value: EventTypeEnum.SINGLES,
          },
          {
            label: 'Mixed',
            value: 'MIXED',
          },
        ];
      } else {
        return [
          {
            label: 'Doubles',
            value: EventTypeEnum.DOUBLES,
          },
          {
            label: 'Singles',
            value: EventTypeEnum.SINGLES,
          },
        ];
      }
    } else if (formatType === 'TEAM') {
      return [
        {
          label: 'Doubles',
          value: EventTypeEnum.DOUBLES,
        },
      ];
    }
  }
};

export const getFormatTypeOptions = (
  isJunior: boolean,
  isNtrp: boolean,
  isFamily: boolean,
  isWheelchair: boolean,
): Option[] => {
  if (isFamily) {
    return [
      {
        label: 'Team',
        value: 'TEAM',
      },
    ];
  } else if (isJunior) {
    return [];
  } else if (isNtrp || isWheelchair) {
    return [
      {
        label: 'Individual',
        value: 'INDIVIDUAL',
      },
    ];
  } else {
    return [
      {
        label: 'Individual',
        value: 'INDIVIDUAL',
      },
      {
        label: 'Team',
        value: 'TEAM',
      },
    ];
  }
};
export const getListTypeOptions = (isJunior: boolean): Option[] => {
  if (isJunior) {
    return [
      {
        label: 'Seeding',
        value: ListTypeEnum.SEEDING,
      },
      {
        label: 'Standing',
        value: ListTypeEnum.STANDING,
      },
    ];
  } else {
    return [
      {
        label: 'Standing',
        value: ListTypeEnum.STANDING,
      },
    ];
  }
};

export const getGenderOptions = (isJunior: boolean) => {
  if (isJunior) {
    return [
      {
        label: 'Boys',
        value: GenderEnum.MALE,
      },
      {
        label: 'Girls',
        value: GenderEnum.FEMALE,
      },
    ];
  } else {
    return [
      {
        label: 'Mens',
        value: GenderEnum.MALE,
      },
      {
        label: 'Womens',
        value: GenderEnum.FEMALE,
      },
    ];
  }
};

export const getAvailablePublishDates = (lists: RanklistInterface[]) => {
  return lists && lists.length > 0
    ? lists?.map((list) => {
        const label = formatDateTime(list?.publishDate, {
          dateStyle: 'medium',
          timeStyle: 'short',
        });
        return { label, value: list.id };
      })
    : [{ label: 'No available dates', value: '0' }];
};

//TODO: COMP-8470 rework this
export const mapRankingDataToScaleItems = (
  rankingData: any,
  event: Event,
  participants,
  teamParticipants: any,
  organisationParticipantIds: any,
  listGender: any,
  isMixed: boolean,
  isTeamFormat: boolean,
  isWheelchair: boolean,
) => {
  const participantIds = isTeamFormat ? teamParticipants : organisationParticipantIds.flat();
  const rankings = participantIds?.map((eventParticipants): any => {
    const participant = participants?.flat()?.find((p) => {
      return isTeamFormat
        ? null
        : p?.person?.personOtherIds?.find((poid) => poid?.personId === eventParticipants?.personId);
    });

    const ranking = rankingData.find((ustaRanking: any) => {
      if (isTeamFormat) {
        return (
          (ustaRanking?.participants[0]?.itemId === eventParticipants[0]?.personId &&
            ustaRanking?.participants[1]?.itemId === eventParticipants[1]?.personId) ||
          (ustaRanking?.participants[0]?.itemId === eventParticipants[1]?.personId &&
            ustaRanking?.participants[1]?.itemId === eventParticipants[0]?.personId)
        );
      } else {
        return ustaRanking?.participants?.find((p: any) => p.itemId === eventParticipants?.personId);
      }
    });

    const mappedRanking = createParticipantRanking(ranking, event);

    const individualParticipants =
      isTeamFormat &&
      participants
        ?.flat()
        ?.filter((p) =>
          p?.person?.personOtherIds?.find(
            (poid) =>
              poid?.personId === eventParticipants?.[0]?.personId ||
              poid?.personId === eventParticipants?.[1]?.personId,
          ),
        );

    if (individualParticipants?.length) {
      return individualParticipants.map((p) => ({
        participantId: p.participantId,
        ranking: mappedRanking,
      }));
    }

    if (
      (isMixed && (listGender === participant?.person?.sex || (isTeamFormat && !!eventParticipants[0]?.doublesId))) ||
      !isMixed ||
      isWheelchair
    ) {
      return {
        participantId: isTeamFormat ? eventParticipants[0]?.doublesId : participant?.participantId,
        ranking: mappedRanking,
      };
    } else {
      return null;
    }
  });

  return rankings?.flat();
};

//TODO: COMP-8470 rework this
export const mapWtnDataToScaleItems = (
  wtnData: any,
  eventTypes: EventTypeEnum[],
  participants: ParticipantInterface[],
) => {
  const personIdAsNationalId = getClientConfig().scalingTypes.some((configItem) => {
    return configItem[0] === 'WTN' && configItem[1]?.personIdAsNationalId;
  });

  return wtnData?.worldTennisNumber
    ?.map((wtnRecord: any) => {
      const nationalId = wtnRecord?.nationalID;
      if (!nationalId) return null;

      const participant = participants.find((p) => {
        if (personIdAsNationalId) return p.person?.personId === nationalId;
        return p.person?.personOtherIds?.some((poid) => poid?.personId === nationalId);
      });
      if (!participant) return null;

      const mappedRanking = eventTypes.map((eventType) => createParticipantWtnRating(wtnRecord, eventType));
      return {
        participantId: participant?.participantId,
        wtnRating: mappedRanking,
      };
    })
    .filter(Boolean);
};

export const mapPtDataToScaleItems = (ptData: PtData, participants, organisationParticipantIds: any) => {
  return organisationParticipantIds.map((eventParticipantId): any => {
    const participant = participants?.find((participant) =>
      participant?.person?.personOtherIds?.find((poid) => poid?.personId === eventParticipantId?.personId),
    );
    const ptPoints = ptData?.points?.find((ptItem) => {
      return ptItem?.person?.username === eventParticipantId?.personId;
    });

    const mappedPoints = createParticipantPtPoints(ptPoints);
    return {
      participantId: participant?.participantId,
      ptPoints: mappedPoints,
    };
  });
};

const createParticipantRanking = (ranking: any, event: any) => {
  const { category } = event;
  const rankingTimeItem: any = {
    itemType: 'RANKING',
    itemValue: {
      ageCategoryCode: category?.ageCategoryCode,
      eventSubType: event.eventType === EventTypeEnum?.TEAM ? EventTypeEnum.SINGLES : event.eventType,
      eventType: category?.subType,
      ranking: ranking?.rank,
      rankingDate: new Date(),
    },
  };
  return rankingTimeItem;
};

const createParticipantWtnRating = (wtnRating: any, eventType: EventTypeEnum) => {
  const wtnData: any = wtnRating?.worldTennisNumbers?.find((worldTennisNumber: any) => {
    return ['MATCHUPS', 'SEED'].includes(worldTennisNumber?.source) && worldTennisNumber?.type === WtnType[eventType];
  });
  const rankingTimeItem: any = {
    itemType: 'RATING',
    itemValue: {
      wtnRating: wtnData?.tennisNumber,
      confidence: wtnData?.confidence,
      gameZoneLower: wtnData?.gameZoneLower,
      gameZoneUpper: wtnData?.gameZoneUpper,
      isRanked: wtnData?.isRanked,
    },
    eventType,
    itemDate: wtnData?.ratingDate,
  };
  return rankingTimeItem;
};

const createParticipantPtPoints = (ptPoints: PtItem) => {
  return {
    itemType: 'RATING',
    itemValue: ptPoints?.points,
  };
};

export const getAgeRestriction = (category, isJunior) => {
  const { ageCategoryCode } = category || {};
  let age;

  if (isJunior) {
    age = ageCategoryCode?.split('U')[1];
  } else {
    age = ageCategoryCode?.split('O')[1];
  }
  const ageRestriction = 'y' + age;
  return Number(age) > 11 ? ageRestriction : '';
};
